import React from 'react'
import classNames from 'classnames'
import { connect } from 'react-redux'

import { RouterState } from 'connected-react-router'
import { useTranslation } from 'react-i18next'

import {
  Formik,
  FormikHelpers,
  Form,
  Field
} from 'formik'

import {
  AppState,
  RootState,
  Dispatcher,
  StoreUserState,
  LoginFormValue
} from 'AppTypes'

import { toggleGlobalLoading, switchLanguage } from '@app/state_management/actions'
import { storeUserActions } from '@features/store_user/state_management/actions'

import { LoginFormValidationSchema } from '@features/store_user/validations'

import { toast } from '@services'

type Props = {
  appState: AppState
  storeUserState: StoreUserState
  dispatch: Dispatcher
  routerState: RouterState
}

/* eslint no-console: "off" */
/* eslint no-debugger: "off" */
const Login: React.FC<Props> = ({
  routerState: {
    location: {
      query: locationQuery
    }
  },
  appState: {
    isLoading,
    language
  },
  dispatch
}) => {
  const { t } = useTranslation()

  const loginFormInitialValues: LoginFormValue = {
    userName: locationQuery.userName || '',
    password: '',
    rememberMe: true
  }

  const dispatchAuthenticate = async (values: LoginFormValue, formikActions: FormikHelpers<LoginFormValue>) => {
    dispatch(toggleGlobalLoading(true))

    const action = await dispatch(storeUserActions.authenticate({
      userName: values.userName,
      password: values.password
    }))

    dispatch(toggleGlobalLoading(false))

    if (action.meta.requestStatus === 'fulfilled') {
      const redirectToUrl = decodeURIComponent(locationQuery.redirectTo || encodeURIComponent('/dashboard'))
      window.location.replace(redirectToUrl)
    } else {
      formikActions.setErrors({
        userName: t('validation.default.invalidUserNameOrPassword')
      })
      toast.error(t('messages.invalidUserNameOrPasswordMsg'))
    }
  }

  return (
    <div id="login" style={{ flexDirection: 'column' }}>
      <section className="login-block">
        <div className="row no-gutters align-items-center">
          <div className="col-4 text-center login-logo">
            <img src="/assets/img/GMTS_logo.png" alt="" />
          </div>
          <div className="col-8 login-content p-4">
            <h1>{`GMTS ${t('auth.login')}`}</h1>
            <Formik
              initialValues={loginFormInitialValues}
              validationSchema={LoginFormValidationSchema}
              onSubmit={async (values: LoginFormValue, actions: FormikHelpers<LoginFormValue>) => {
                await dispatchAuthenticate(values, actions)
              }}
            >
              {({ errors, touched }) => (
                <Form className="login-box login-form p-5">
                  <div className="form-group mb-3">
                    <Field name="userName" className={classNames('form-control', { error: errors.userName && touched.userName })} placeholder={t('auth.userNameLabel')} type="text" />
                    {errors.userName && touched.userName ? (
                      <div className="validation-error">{t(errors.userName)}</div>
                    ) : null}
                  </div>
                  <div className="form-group mb-3">
                    <Field name="password" className={classNames('form-control', { error: errors.password && touched.password })} placeholder={t('auth.passwordLabel')} type="password" />
                    {errors.password && touched.password ? (
                      <div className="validation-error">{t(errors.password)}</div>
                    ) : null}
                  </div>
                  <div className="form-group mb-3">
                    <div className="custom-control custom-checkbox">
                      <Field id="rememberMe" type="checkbox" className="custom-control-input" name="rememberMe" />
                      <label htmlFor="rememberMe" className="custom-control-label custom-checkbox-login">{t('auth.rememberMe')}</label>
                    </div>
                  </div>
                  <div className="form-group text-center mt-4 mb-0">
                    <button className="btn btn-search w-50" type="submit" disabled={isLoading}>
                      {t('auth.login')}
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          </div>
        </div>
      </section>
      <div aria-hidden="true" className="plaintext-button" onClick={() => dispatch(switchLanguage(language === 'jp' ? 'en' : 'jp'))}>
        {language === 'jp' ? 'English' : '日本語'}
      </div>
    </div>
  )
}

export default connect((state: RootState) => ({
  appState: state.app,
  storeUserState: state.storeUser,
  routerState: state.router
}))(Login)
